import { IFlight, IHighlightFlightSettings } from "@vmosolutions/oc-interfaces";
import { Camera } from "../../../../SharedGL/Camera";
import { findClosestFlightIndex, hexToRgb, getFlightAlphaValue } from "../../TimelineUtils";
import { FlightBuffer } from "./FlightBuffer";
import { FlightShader } from "./FlightShader";

interface IFlightGLAttributes {
  position: WebGLBuffer;
  color: WebGLBuffer;
}

export interface IDelayedFlight {
  departure: Date;
  arrival: Date;
  color: string;
}

/** Add more injected settings here as needed */
export interface ISettings {
  delayBufferDeparture: number;
  delayBufferArrival: number;
  icao: boolean;
  utc: boolean;
  currentDateOffsetHours: number;
  viewScale: string;
  overdueDepartureMovementMsgMinutes: number | null;
  overdueArrivalMovementMsgMinutes: number | null;
}

export class FlightGL {
  //The aircraft_details styles class is dependant on the aircraftRowHeight
  //The FlightErrors are dependant on the aircraftRowHeight
  public static rowHeightPercentage = 1.0;

  public static aircraftRowHeight = 36 + 6;
  public static aircraftRowPadding = 1;
  public static actualDepartureStart = 6 + 6;
  public static actualDepartureHeight = 20;

  public static scheduledStart = 2;
  public static scheduledHeight = 4 + 6;

  public static aircraftRowTotalHeight: number = FlightGL.aircraftRowHeight + FlightGL.aircraftRowPadding;

  private delayBufferDeparture = 0;
  private delayBufferArrival = 0;

  // public static readonly referenceTime = new Date().getTime();

  private actualFlights: FlightBuffer;
  private scheduledFlights: FlightBuffer;
  private delayedFlights: IFlight[] = [];

  protected buffers: IFlightGLAttributes;

  private vertexCount = 0;
  private scheduledFlightsVertexCount = 0;

  private flightIndexesInRange: number[] = [0, 0];
  private flightsInRange: IFlight[] = [];

  private flightData: IFlight[] = [];

  constructor(gl: WebGLRenderingContext, flightShader: FlightShader, settings: ISettings) {
    this.actualFlights = new FlightBuffer(gl, flightShader);
    this.scheduledFlights = new FlightBuffer(gl, flightShader);
    this.delayBufferDeparture = settings?.delayBufferDeparture ?? 0;
    this.delayBufferArrival = settings?.delayBufferArrival ?? 0;
  }

  public updateBuffers = (
    gl: WebGLRenderingContext,
    flightData: IFlight[],
    highlightFlightSettings?: IHighlightFlightSettings
  ): void => {
    this.flightData = flightData;
    this.flightIndexesInRange = [0, flightData.length - 1];
    this.delayedFlights = [];
    const actualFlightPositions: number[] = [];
    const actualFlightColors: number[] = [];

    const scheduledFlightPositions: number[] = [];
    const scheduledFlightColors: number[] = [];

    // iterating over an aircraft (row) so height will always be same
    // const y0 = this.flightGLRowHeightSettings.actualDepartureStart;
    // const y = y0 + this.flightGLRowHeightSettings.actualDepartureHeight;

    // const scheduledYStart = this.flightGLRowHeightSettings.scheduledStart;
    // const scehduledYEnd = scheduledYStart + this.flightGLRowHeightSettings.scheduledHeight;
    const y0 = FlightGL.actualDepartureStart;
    const y = y0 + FlightGL.actualDepartureHeight;

    const scheduledYStart = FlightGL.scheduledStart;
    const scehduledYEnd = scheduledYStart + FlightGL.scheduledHeight;

    // debugger;
    flightData.forEach((flight: IFlight) => {
      const outDelay = flight.actualOut?.valueOf() - flight.scheduledOut?.valueOf();
      const inDelay = flight.actualIn?.valueOf() - flight.scheduledIn?.valueOf();
      // const flightDelay = Math.max(outDelay, inDelay);
      const [r, g, b] = flight.flightType ? hexToRgb(flight.flightType.color) : [1, 1, 1];

      // If highlightFlights boolean is true and there are conditions passed in, set flight alpha based on those user supplied conditions;
      const flightAlpha = getFlightAlphaValue(flight, highlightFlightSettings);

      /*Actual Flight info */
      {
        // positions
        const x0 = flight.actualOut?.valueOf() - Camera.referenceTime; //Block Time switch to Out/In
        const x = flight.actualIn?.valueOf() - Camera.referenceTime; //Block Time switch to Out/In
        const positions = [x0, y0, x, y0, x, y, x0, y0, x, y, x0, y];
        actualFlightPositions.push(...positions);

        // colors = If delayed flights exceed allowable departure or arrival delay buffers (minutes converted to milli) set delayed color for flights
        const color =
          outDelay > this.delayBufferDeparture * 60000 || inDelay > this.delayBufferArrival * 60000
            ? [0.8, 0.1, 0.1, flightAlpha]
            : [r, g, b, flightAlpha];
        const colors = [...color, ...color, ...color, ...color, ...color, ...color];

        actualFlightColors.push(...colors);
      }

      /*Scheduled Flight info */
      if (outDelay !== 0 || inDelay !== 0) {
        const departure = flight.scheduledOut; //Block Time switch to Out/In
        const arrival = flight.scheduledIn; //Block Time switch to Out/In

        this.delayedFlights.push({ ...flight }); // ({ departure, arrival, color: flightColor });
        // positions
        const x0 = departure?.valueOf() - Camera.referenceTime;
        const x = arrival?.valueOf() - Camera.referenceTime;
        // debugger;
        const scheduledPositions = [
          x0,
          scheduledYStart,
          x,
          scheduledYStart,
          x,
          scehduledYEnd,

          x0,
          scheduledYStart,
          x,
          scehduledYEnd,
          x0,
          scehduledYEnd,
        ];
        scheduledFlightPositions.push(...scheduledPositions);

        const scheduledColors = [
          r,
          g,
          b,
          flightAlpha,
          r,
          g,
          b,
          flightAlpha,
          r,
          g,
          b,
          flightAlpha,
          r,
          g,
          b,
          flightAlpha,
          r,
          g,
          b,
          flightAlpha,
          r,
          g,
          b,
          flightAlpha,
        ];

        scheduledFlightColors.push(...scheduledColors);
      }
    });

    this.actualFlights.updateBuffers(gl, actualFlightPositions, actualFlightColors);
    this.scheduledFlights.updateBuffers(gl, scheduledFlightPositions, scheduledFlightColors);
    this.vertexCount = actualFlightPositions.length / 2;
    this.scheduledFlightsVertexCount = scheduledFlightPositions.length / 2;
  };

  public getDelayedFlights = (): IFlight[] => {
    return this.delayedFlights;
  };

  public getFlightIndexes = (): IFlight[] => {
    return this.flightsInRange;
  };

  public updateFlightsInRange = (camera: Camera): number[] => {
    if (this.flightData.length === 0) {
      return [0, 0];
    }
    const [minTime, maxTime] = camera.getXRange();

    const min = findClosestFlightIndex(this.flightData, minTime, "actualIn"); //Block Time switch to Out/In
    const max = findClosestFlightIndex(this.flightData, maxTime, "actualOut"); //Block Time switch to Out/In
    // Add a buffer of one flight on either side if possible
    this.flightIndexesInRange = [Math.max(0, min - 1), Math.min(max + 1, this.flightData.length - 1)];
    return this.flightIndexesInRange;
  };

  public getDelayedFlightsInRange = (camera: Camera): number[] => {
    if (this.flightData.length === 0) {
      return [0, 0];
    }
    const [minTime, maxTime] = camera.getXRange();

    // const inRange = (delayedFlight: IDelayedFlight) => {
    //   return delayedFlight.arrival.getTime() >= minTime && delayedFlight.departure.getTime() < maxTime
    // };
    // let minIndex = -1;
    // for (let i = 0; i < this.delayedFlights.length; i++) {
    //   if (inRange(this.delayedFlights[i])) {
    //     minIndex = i;
    //     break;
    //   }
    // }

    // let maxIndex = -1;
    // for (let i = this.delayedFlights.length - 1; i >= 0; i--) {
    //   if (inRange(this.delayedFlights[i])) {
    //     maxIndex = i;
    //     break;
    //   }
    // }

    const min = findClosestFlightIndex(this.delayedFlights, minTime, "actualIn"); //Block Time switch to Out/In
    const max = findClosestFlightIndex(this.delayedFlights, maxTime, "actualOut"); //Block Time switch to Out/In

    return [min, max];
  };

  public getFlightsInRange = (): IFlight[] => {
    return this.flightsInRange;
  };

  public getFlightIndexesInRange = (): number[] => {
    return this.flightIndexesInRange;
  };

  public getFlights = (): IFlight[] => {
    return this.flightData;
  };

  public draw = (gl: WebGLRenderingContext, camera: Camera, yStart: number): void => {
    const [minIndex, maxIndex] = this.flightIndexesInRange;
    if (this.vertexCount === 0 || minIndex < 0 || maxIndex < 0) {
      return;
    }
    const offset = minIndex * 6;
    const numVerts = (maxIndex + 1) * 6 - offset;
    if (numVerts > 0 && numVerts <= this.vertexCount) {
      this.actualFlights.draw(gl, camera, yStart, offset, numVerts);
    }

    if (this.scheduledFlightsVertexCount === 0) {
      return;
    }
    this.scheduledFlights.draw(gl, camera, yStart, 0, this.scheduledFlightsVertexCount);
  };
}
